<template>
  <div
    class="button-group"
    :class="props.class"
  >
    <BaseButton
      v-for="(button, index) in buttons"
      v-bind="button"
      :key="button.key || index"
      :variant="button.key === props.modelValue ? selectedVariant : props.variant"
      :bordered="props.bordered"
      :rounded="props.rounded"
      :size="props.size"
      :fluid="props.fluid"
      :isMobile="props.isMobile"
      :disabled="props.disabledGroup || button.disabled"
      :class="{ selected: isSelected(button.key) }"
      @on-click="handleClick(button)"
    />
  </div>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/Base/BaseButton.vue';
import type { ButtonVariant, IButton } from '@/components/Base/BaseButton.vue';
import { watch } from 'vue';

interface IButtons extends IButton {
  key: string | number;
  value: any;
}

interface IButtonGroup extends Partial<Pick<IButton, 'variant' | 'size' | 'bordered' | 'rounded' | 'isMobile' | 'fluid'>> {
  buttons: IButtons[];
  modelValue?: string | number | string[] | number[];
  direction?: 'row' | 'column';
  multiple?: boolean;
  selectedVariant?: ButtonVariant;
  class?: string;
  disabledGroup?: boolean;
}

const props = withDefaults(defineProps<IButtonGroup>(), {
  direction: 'row',
  multiple: false,
  variant: 'primary',
  selectedVariant: 'primary',
  class: '',
  disabledGroup: false,
  bordered: false,
  rounded: false
});

const emit = defineEmits(['click', 'update:modelValue']);
function handleClick(button: IButtons) {
  if (props.disabledGroup || button.disabled) return;
  if (props.multiple && Array.isArray(props.modelValue)) {
    const selectedValuesArr = props.modelValue as string[] | number[];
    const index = selectedValuesArr.indexOf(button.key as never);
    if (index === -1) {
      emit('update:modelValue', [...selectedValuesArr, button.key]);
    } else {
      emit(
        'update:modelValue',
        selectedValuesArr.filter((key) => key !== button.key)
      );
    }
  } else {
    emit('update:modelValue', button.key);
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if ((Array.isArray(newValue) && !newValue.length) || !newValue) return;
    if (Array.isArray(newValue)) {
      const selectedValueObj = props.buttons.filter((btn) =>
        (newValue as string[] | number[]).includes(btn.key as never)
      );
      emit('click', selectedValueObj);
    } else {
      const selectedValueObj = props.buttons.find((btn) => btn.key === newValue);
      emit('click', selectedValueObj);
    }
  },
  { immediate: false }
);

const isSelected = (key: string | number) => {
  if (Array.isArray(props.modelValue)) {
    return (props.modelValue as string[] | number[]).includes(key as never);
  }
  return key === props.modelValue;
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  flex-direction: v-bind('props.direction');
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
</style>
